import { IDevice } from './DevicesManagementPage';
import API from '../../../utils/axios.utils';

function getAuthHeaders() {
  const token = sessionStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Interface for device update data sent to the API
 */
type DeviceUpdateData = {
  primary_company_id?: number | null;
  secondary_company_id?: number | null;
  tertiary_company_id?: number | null;
};

/**
 * Interface for device update API response
 */
interface DeviceUpdateResponse {
  device_id: number;
  serial: string;
  primary_company: { edgelink_company_id: number; name: string } | null;
  secondary_company: { edgelink_company_id: number; name: string } | null;
  tertiary_company: { edgelink_company_id: number; name: string } | null;
}

/**
 * API device data interface
 */
interface ApiDeviceData {
  device_id: number;
  serial: string;
  alias?: string;
  location?: string;
  last_check_in: string;
  connection_state: string;
  primary_company?: { edgelink_company_id: number; name: string } | null;
  secondary_company?: { edgelink_company_id: number; name: string } | null;
  tertiary_company?: { edgelink_company_id: number; name: string } | null;
}

/**
 * Fetch all devices with optional filter for inactive devices
 * @param includeInactive - Whether to include inactive devices
 * @returns Promise with array of devices
 */
export const getDevices = async (includeInactive = true): Promise<IDevice[]> => {
  try {
    const response = await API.get<ApiDeviceData[]>('/api/admin/edgelink/devices/', {
      headers: getAuthHeaders(),
    });

    // Transform API response to match IDevice interface
    return response.data
      .map((device) => ({
        id: device.device_id,
        serial: device.serial,
        location: device.location || 'Unknown location',
        last_check_in: device.last_check_in,
        primary_company: device.primary_company?.name || '',
        secondary_company: device.secondary_company?.name || null,
        tertiary_company: device.tertiary_company?.name || null,
        status: device.connection_state === 'online', // Consider 'online' as active status
      }))
      .filter((device) => includeInactive || device.status); // Apply client-side filter if needed
  } catch (error) {
    console.error('Error fetching devices:', error);
    return [];
  }
};

/**
 * Company data interface
 */
export interface ICompany {
  edgelink_company_id: number;
  name: string;
  main_contact: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  status: boolean;
}

/**
 * Fetch all companies
 * @returns Promise with array of companies
 */
export const getCompanies = async (): Promise<ICompany[]> => {
  try {
    const response = await API.get<ICompany[]>('/api/admin/edgelink/companies/', {
      headers: getAuthHeaders(),
    });

    // Return the data directly
    return response.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    return [];
  }
};

/**
 * Update device company information
 * @param deviceId - The device ID
 * @param data - The data to update (company information)
 * @returns Promise with updated device data
 */
export const updateDevice = async (deviceId: number, data: DeviceUpdateData): Promise<IDevice | null> => {
  try {
    // Construct the API URL
    const url = `/api/admin/edgelink/devices/${deviceId}/`;

    // Send PUT request to update device
    const response = await API.put(url, data, {
      headers: getAuthHeaders(),
    });

    // Transform the API response to match IDevice interface
    if (response.data) {
      const responseData = response.data as DeviceUpdateResponse;
      return {
        id: responseData.device_id,
        serial: responseData.serial,
        location: 'Unknown location', // Location is not included in the response
        last_check_in: '', // Last check-in is not included in the response
        primary_company: responseData.primary_company?.name || '',
        secondary_company: responseData.secondary_company?.name || null,
        tertiary_company: responseData.tertiary_company?.name || null,
      };
    }

    return null;
  } catch (error) {
    console.error('Error updating device:', error);
    throw error; // Throw the error to be handled by the component
  }
};
